import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import { useState, useEffect, type KeyboardEvent, type ReactNode } from "react";
import { MdClose } from "react-icons/md";
import "@style/Admin.scss";
import "@style/sass/_selectModal.scss";
import Modal from "@modalSrc/Modal"; 

type SelectModalProps = {
  isOpen: boolean;
  onSelect?: (item: unknown | null) => void;
  onCancel?: () => void;
  title?: string;
  message?: string | ReactNode;
  items?: unknown[];
  itemLabelKey?: string;
  itemValueKey?: string;
  selectText?: string;
  cancelText?: string;
  allowEmpty?: boolean;
  emptySelectionText?: string;
  caption: string;
  modalClassName?: string;
};

export default function SelectModal({
  isOpen,
  onSelect,
  onCancel,
  title = "Select an Option",
  message = "Please choose from the following options:",
  items = [],
  itemLabelKey = "name",
  itemValueKey = "id",
  selectText = "Select",
  cancelText = "Cancel",
  allowEmpty = false,
  emptySelectionText = "None",
  caption = "",
  modalClassName = "",
}: SelectModalProps) {
  const [selectedItem, setSelectedItem] = useState<unknown | null>(null);

  // Reset selection when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedItem(null);
    }
  }, [isOpen]);

  const handleSelect = () => {
    if (onSelect) {
      if (selectedItem === null && allowEmpty) {
        onSelect(null);
      } else if (selectedItem !== null) {
        onSelect(selectedItem);
      }
    }
  };

  const handleCancel = () => {
    setSelectedItem(null);
    if (onCancel) onCancel();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      if (selectedItem !== null || allowEmpty) {
        handleSelect();
      }
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleItemSelect = (item: unknown) => {
    setSelectedItem(item);
  };

  if (!isOpen) {
    return null;
  }

  const getItemLabel = (item: unknown) => {
    if (typeof item === 'string') return item;
    if (typeof item === 'object' && item !== null) {
      const map = item as Record<string, unknown>;
      const labelValue =
        map[itemLabelKey] ??
        map.name ??
        map.label ??
        map.title ??
        map[itemValueKey] ??
        map.id;
      return labelValue != null ? String(labelValue) : String(item);
    }
    return String(item);
  };

  const bodyContent = (
    <div className="selectModalContent">
      <div className="tableWrapper dimensionTable">
        <table>
          <caption>{caption}</caption>
          <thead>
            <tr>
                <th>
                    {typeof message === 'string' ? parseSanitizedHtml(message) : message}
                </th>
            </tr>
          </thead>
          <tbody>
            {allowEmpty && (
              <tr 
                className={selectedItem === null ? 'active' : ''}
                onClick={() => setSelectedItem(null)}
                style={{ cursor: 'pointer' }}
              >
                <td>{emptySelectionText}</td>
              </tr>
            )}
            
            {items.map((item, index) => {
              const label = getItemLabel(item);
              const isSelected = selectedItem === item;
              
              return (
                <tr 
                  key={index}
                  className={isSelected ? 'active' : ''}
                  onClick={() => handleItemSelect(item)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{label}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const footer = (
    <div className="footerRight">
      <button
        type="button"
        className="btn btn-secondary"
        onClick={handleCancel}
      >
        {cancelText}
      </button>
      <button
        type="button"
        className="btn btn-primary okButton"
        onClick={handleSelect}
        disabled={selectedItem === null && !allowEmpty}
      >
        {selectText}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      title={title}
      onClose={handleCancel}
      footer={footer}
      className={["selectModal", modalClassName].filter(Boolean).join(" ")}
    >
      {bodyContent}
    </Modal>
  );
}

